import { useState } from "react";
import { applySeries } from "@/lib/catalogue";
import { Layers } from "lucide-react";
import { TextField, Field } from "@/components/Fields";
import { PROFILE_PRESETS } from "@/lib/geometry";
import { GLASS_TYPES, GLASS_PRESETS, composeGlass } from "@/lib/typology";
import { INFILL_PRESETS } from "@/lib/infill";
import { catalogueFor, allSeriesNames } from "@/lib/catalogue";
import { useCatalogues } from "@/context/CatalogueContext";
import { NumField } from "@/components/Fields";
import { DetailPicker } from "@/components/DetailPicker";

const SeriesCard = ({ item, update }) => {
  const c = catalogueFor(item.profile_system);
  return (
    <div className="rounded-md border border-slate-700 p-3 bg-[#0B0F19]/60 space-y-2" data-testid="series-card">
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">
        {c ? (c.user ? "From your uploaded YKK AP catalogue" : c.iwin ? "Official IWIN-S values (Bhoruka · YKK AP)" : "Built-in YKK AP values (approximate)") : `Custom system · ${allSeriesNames().length} series available`}
      </p>
      {c && <p className="font-mono text-xs text-cyan-300" data-testid="series-values">Frame {c.frame} · Mullion {c.mullion} · Sash {c.sash} · Depth {c.depth} mm</p>}
      {c?.user && Object.values(c.codes || {}).some(Boolean) && (
        <p className="font-mono text-[11px] text-slate-300" data-testid="series-codes">{Object.entries(c.codes).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
      )}
      <NumField label="Frame depth (mm, for sections)" value={item.frame_depth ?? 60} onChange={(v) => update({ ...item, frame_depth: v })} testid="frame-depth-input" min={30} />
      <p className="text-[10px] text-slate-500">{c?.user ? "Catalogue values" : "Approximate values — upload the YKK AP catalogue (header › Catalogue) for exact data"}. Face widths remain editable in the Geometry tab.</p>
    </div>
  );
};

const FINISH = ["RAL 9016 Traffic white", "RAL 7016 Anthracite grey", "RAL 9005 Jet black", "Silver anodised", "Champagne anodised", "Wood-grain powder coat"];

const DataList = ({ id, opts }) => <datalist id={id}>{opts.map((o) => <option key={o} value={o} />)}</datalist>;
const sel = "h-9 w-full rounded-md bg-[#0B0F19] border border-slate-700 px-2 text-sm text-slate-100 focus:outline-none focus:border-cyan-500";

const GlassBuilder = ({ onApply }) => {
  const [g, setG] = useState({ construction: "dgu", outer: 6, outerType: "Clear toughened", gap: 12, inner: 6, innerType: "Clear" });
  const s = (k) => (e) => setG({ ...g, [k]: e.target.value });
  const num = (k, testid) => <input data-testid={testid} type="number" min={1} value={g[k]} onChange={s(k)} className={sel} />;
  const type = (k, testid) => <select data-testid={testid} value={g[k]} onChange={s(k)} className={sel}>{GLASS_TYPES.map((t) => <option key={t}>{t}</option>)}</select>;
  return (
    <div className="rounded-md border border-slate-700 p-3 space-y-3 bg-[#0B0F19]/60" data-testid="glass-builder">
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Layers size={12} />Glass builder</p>
      <div className="grid grid-cols-2 h-8 rounded-md border border-slate-700 overflow-hidden">
        {[["single", "Single glazed"], ["dgu", "Double glazed (DGU)"]].map(([v, l]) => (
          <button key={v} data-testid={`glass-construction-${v}`} onClick={() => setG({ ...g, construction: v })}
            className={`text-[11px] transition-colors ${g.construction === v ? "bg-cyan-500 text-slate-950" : "text-slate-400 hover:bg-slate-800"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-[70px_1fr] gap-2">
        <Field label="Outer mm">{num("outer", "glass-outer-thk")}</Field><Field label="Outer pane">{type("outerType", "glass-outer-type")}</Field>
        {g.construction === "dgu" && <>
          <Field label="Air gap">{num("gap", "glass-gap")}</Field><div />
          <Field label="Inner mm">{num("inner", "glass-inner-thk")}</Field><Field label="Inner pane">{type("innerType", "glass-inner-type")}</Field>
        </>}
      </div>
      <button data-testid="glass-apply-btn" onClick={() => onApply(composeGlass(g))}
        className="w-full h-8 rounded-md bg-cyan-500 text-slate-950 text-xs font-semibold hover:bg-cyan-400 transition-colors">Use: {composeGlass(g)}</button>
    </div>
  );
};

export const SpecsTab = ({ item, update }) => {
  useCatalogues();
  const set = (k) => (v) => update({ ...item, [k]: v });
  return (
    <div className="space-y-4">
      <TextField label="Aluminium system (standard: YKK AP)" value={item.profile_system} onChange={(v) => update(applySeries({ ...item, profile_system: v }))} testid="profile-system-input" list="dl-profiles" />
      <SeriesCard item={item} update={update} />
      <DetailPicker item={item} update={update} />
      <TextField label="Glass / infill specification (all panels)" value={item.glass_spec} onChange={set("glass_spec")} testid="glass-spec-input" list="dl-glass" />
      <GlassBuilder onApply={set("glass_spec")} />
      <p className="text-[11px] text-slate-500">Different glass for a single panel (e.g. frosted for a toilet)? Set it in the Panels tab.</p>
      <TextField label="Finish / colour" value={item.finish} onChange={set("finish")} testid="finish-input" list="dl-finish" />
      <TextField label="Hardware" value={item.hardware} onChange={set("hardware")} testid="hardware-input" />
      <TextField label="Item notes" value={item.notes} onChange={set("notes")} testid="item-notes-input" area />
      <DataList id="dl-profiles" opts={[...new Set([...allSeriesNames(), ...PROFILE_PRESETS])]} /><DataList id="dl-glass" opts={[...GLASS_PRESETS, ...INFILL_PRESETS]} /><DataList id="dl-finish" opts={FINISH} />
    </div>
  );
};

const TB_FIELDS = [["company", "Company / fabricator"], ["project_name", "Project name"], ["client", "Client"], ["site", "Site / address"], ["architect", "Architect / consultant"], ["contractor", "Contractor"], ["drawing_no", "Drawing no. prefix"], ["revision", "Revision"], ["date", "Date"], ["drawn_by", "Drawn by"], ["checked_by", "Checked by"]];

export const TitleTab = ({ tb, setTb }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-3">
      {TB_FIELDS.map(([k, l]) => <TextField key={k} label={l} value={tb[k]} onChange={(v) => setTb({ ...tb, [k]: v })} testid={`tb-${k}-input`} />)}
    </div>
    <TextField label="General notes" value={tb.notes} onChange={(v) => setTb({ ...tb, notes: v })} testid="tb-notes-input" area />
    <label className="flex items-center gap-3 p-3 rounded-md border border-slate-800 bg-[#0d1220] cursor-pointer" data-testid="tb-logo-toggle">
      <input type="checkbox" checked={!tb.hide_logo} onChange={(e) => setTb({ ...tb, hide_logo: !e.target.checked })} data-testid="tb-logo-checkbox" className="accent-cyan-500 w-4 h-4" />
      <img src="/brand/logo-light.png" alt="Artisan Guild Infra" className="h-6 w-auto" />
      <span className="text-xs text-slate-400 ml-auto">Logo on every drawing sheet</span>
    </label>
  </div>
);
